import type { Dictionary } from "@kensio/pinyinjs";

import {
  defaultTier,
  DictionaryLoader,
  type ServedTier,
} from "./dictionary.js";
import { element } from "./dom.js";
import type { TierOffer } from "./tier-offer.js";

/**
 * Fetching a dictionary tier, and saying so while it happens.
 *
 * The download is the one part of these tools a visitor can be kept waiting
 * by, so it is also the one part with something to report. The status line
 * says what is happening in words rather than showing a spinner: on a slow
 * connection "Loading the standard dictionary" is the difference between
 * waiting and leaving.
 *
 * Two offers rather than one, since the bigger dictionary began outliving the
 * page it was loaded on. Somebody who has it on the device gets it back
 * without being asked, so they also get a way of saying they would rather not
 * have it: an upgrade that could not be undone would be a worse deal than the
 * one they took.
 *
 * **The bigger dictionary belongs to readers with an account.** It is the most
 * expensive thing this site serves per reader, and it is worth signing in for.
 * A reader with no session is offered the sign-in where the button sits and
 * keeps the standard tier, which is the whole of the tool for the great
 * majority of what anybody converts.
 *
 * **A gate on the button rather than on the files.** The three files sit at a
 * versioned path in the site's own static directory and anybody reading the
 * bundle can fetch them. That is accepted: the dictionary is open source in
 * `@kensio/pinyinjs`, and a real gate would mean a CloudFront behaviour of its
 * own with restricted viewer access, or 2.6 MB through an uncached Lambda. See
 * KensioSoftware/chineseboost#337.
 */

/** What the tier names are called when the page says them out loud. */
const tierNames: Readonly<Record<ServedTier, string>> = {
  standard: "standard",
  full: "full",
};

/** What a page's dictionary loading needs to know about the page. */
export interface TierLoadingProps {
  /** Where the artifacts are published, version and all. */
  readonly dictionaryUrl: string;

  /** Where loading, and failing to load, is reported. */
  readonly status: HTMLElement;

  /** Offers the bigger dictionary, while the smaller one is in use. */
  readonly upgrade: TierOffer;

  /** Offers the smaller one back, while the bigger one is in use. */
  readonly revert: TierOffer;

  /**
   * Whether the page was drawn for somebody with an account.
   *
   * Handed in rather than read from the document here, so that what decides
   * this is one thing the page asked once. See `readerSignedIn`.
   */
  readonly signedIn: boolean;
}

/**
 * A page's dictionary, and the status line that reports on it.
 */
export class TierLoading {
  readonly #loader: DictionaryLoader;
  readonly #status: HTMLElement;
  readonly #upgrade: TierOffer;
  readonly #revert: TierOffer;
  readonly #signedIn: boolean;

  /**
   * The tier in use, which the address bar carries.
   *
   * Held here rather than by the tool: the thing that loads a tier is the
   * thing that knows which one is loaded.
   */
  #current: ServedTier = defaultTier;

  constructor(properties: TierLoadingProps) {
    this.#loader = new DictionaryLoader(properties.dictionaryUrl);
    this.#status = properties.status;
    this.#upgrade = properties.upgrade;
    this.#revert = properties.revert;
    this.#signedIn = properties.signedIn;
  }

  #say(message: string): void {
    this.#status.replaceChildren(
      ...(message === "" ? [] : [element("span", { text: message })]),
    );
    this.#status.hidden = message === "";
  }

  /** Which tier is in use. */
  get tier(): ServedTier {
    return this.#current;
  }

  /**
   * Load a tier, returning the dictionary or nothing if it could not be had.
   *
   * A failure is reported on the page rather than thrown: there is nothing the
   * caller could do about it that this has not already done, and a tool with
   * no dictionary is a tool that says so rather than one that crashes.
   */
  async load(tier: ServedTier): Promise<Dictionary | undefined> {
    if (!this.#loader.has(tier)) {
      this.#say(`Loading the ${tierNames[tier]} dictionary…`);
    }

    try {
      const dictionary = await this.#loader.load(tier);
      this.#current = tier;
      this.#say("");

      /*
       * The upgrade block holds the button and the sign-in that stands in its
       * place, and both go with it. Which of the two a reader is shown is the
       * stylesheet's, from `data-cb-session`, so this says only whether there
       * is anything to offer at all.
       */
      this.#upgrade.show(tier === defaultTier);
      this.#revert.show(tier !== defaultTier);

      return dictionary;
    } catch {
      this.#say(
        "The dictionary could not be loaded. Check your connection and reload the page.",
      );

      return undefined;
    }
  }

  /**
   * Say a dictionary is on its way, and settle which one it is.
   *
   * The status line goes up first, before the tier is known, because knowing it
   * means asking the device what it already has and that answer arrives a
   * moment after the page does. A moment is not a wait anybody notices, but it
   * falls before the first word of the status line would otherwise be written,
   * and a page that says nothing while it starts is a page that looks like it
   * is doing nothing. {@link TierLoading.load} names the tier as soon as there
   * is one to name.
   *
   * A link that named a tier is obeyed, including one naming the smaller:
   * somebody who sent a colleague a `?dictionary=standard` link meant it, and it
   * is also what the address bar carries after a revert. Otherwise the bigger
   * dictionary if this device already has it. That is asked of the store rather
   * than of a remembered preference on purpose: a flag saying "they chose full
   * once" would start a 2.6 MB download on arrival whenever the files had since
   * been evicted, which is the very thing the upgrade button exists to stop.
   *
   * **A reader with no session opens on the standard tier whatever the device
   * holds.** Somebody who loaded the bigger dictionary and then signed out
   * would otherwise go on being given it for as long as the files survived,
   * which is the offer standing for a reader it is no longer being made to. The
   * files stay where they are and the tier is theirs again the moment they sign
   * back in. A link naming a tier is still obeyed, since that is somebody
   * saying what a link is for rather than this page deciding.
   */
  async opening(asked: ServedTier | undefined): Promise<ServedTier> {
    this.#say("Loading the dictionary…");

    if (asked !== undefined) {
      return asked;
    }

    if (!this.#signedIn) {
      return defaultTier;
    }

    return (await this.#loader.isStored("full")) ? "full" : defaultTier;
  }

  /**
   * Wire the two offers to the thing that will do the loading.
   *
   * Here rather than in the tool because these are this object's buttons: it
   * holds them, it shows and hides them, and what they do is change the tier it
   * keeps track of. All the tool contributes is what a loaded dictionary is for.
   *
   * The revert forgets before it loads, so that a page closed in the same breath
   * has still had its say. Nothing is unloaded: the page keeps the dictionary it
   * already has, which is what makes the upgrade reappearing an offer that costs
   * nothing to take up again.
   */
  wireOffers(load: (tier: ServedTier) => Promise<void>): void {
    const revert = async (): Promise<void> => {
      await this.#loader.forget("full");
      await load(defaultTier);
    };

    this.#upgrade.onChosen(() => {
      void load("full");
    });

    this.#revert.onChosen(() => {
      void revert();
    });
  }
}
